import type { DocumentData } from "firebase/firestore";
import { compactData, dateValue, stringList, stringValue } from "./firestore";

// Sports + leagues catalog models. Field contracts mirror firestore.rules
// validSportShape / validLeagueShape and the Swift SportModel/LeagueModel.

export type SBotCatalogSource = "custom" | "espn";
export type SBotReviewStatus = "approved" | "pendingReview" | "rejected";
export type SBotCatalogManager = "sbotLabs" | "creator";

export interface SportModel {
  id: string;
  slug: string;
  name: string;
  source: SBotCatalogSource;
  reviewStatus: SBotReviewStatus;
  managedBySBotLabs: boolean;
  createdByUserID?: string;
  espnSportSlug?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface LeagueModel {
  id: string;
  sportID: string;
  slug: string;
  name: string;
  abbreviation: string;
  source: SBotCatalogSource;
  reviewStatus: SBotReviewStatus;
  managedBy: SBotCatalogManager;
  creatorUserID?: string;
  adminUserIDs: string[];
  espnLeagueSlug?: string;
  primaryColorHex?: string;
  secondaryColorHex?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

function parseSource(value: unknown): SBotCatalogSource {
  return value === "espn" ? "espn" : "custom";
}

function parseReviewStatus(value: unknown): SBotReviewStatus {
  return value === "approved" || value === "rejected" ? value : "pendingReview";
}

export function parseSport(id: string, data: DocumentData): SportModel | null {
  const slug = stringValue(data.slug);
  const name = stringValue(data.name);
  if (!slug || !name) return null;
  return {
    id,
    slug,
    name,
    source: parseSource(data.source),
    reviewStatus: parseReviewStatus(data.reviewStatus),
    managedBySBotLabs: data.managedBySBotLabs === true,
    createdByUserID: stringValue(data.createdByUserID),
    espnSportSlug: stringValue(data.espnSportSlug),
    createdAt: dateValue(data.createdAt),
    updatedAt: dateValue(data.updatedAt),
  };
}

export function sportFirestoreData(sport: SportModel): Record<string, unknown> {
  return compactData({
    id: sport.id,
    slug: sport.slug,
    name: sport.name,
    source: sport.source,
    reviewStatus: sport.reviewStatus,
    managedBySBotLabs: sport.managedBySBotLabs,
    createdByUserID: sport.createdByUserID,
    espnSportSlug: sport.espnSportSlug,
    createdAt: sport.createdAt,
    updatedAt: sport.updatedAt,
  });
}

export function parseLeague(id: string, data: DocumentData): LeagueModel | null {
  const sportID = stringValue(data.sportID);
  const slug = stringValue(data.slug);
  const name = stringValue(data.name);
  if (!sportID || !slug || !name) return null;
  return {
    id,
    sportID,
    slug,
    name,
    abbreviation: stringValue(data.abbreviation) ?? "",
    source: parseSource(data.source),
    reviewStatus: parseReviewStatus(data.reviewStatus),
    managedBy: data.managedBy === "sbotLabs" ? "sbotLabs" : "creator",
    creatorUserID: stringValue(data.creatorUserID),
    adminUserIDs: stringList(data.adminUserIDs),
    espnLeagueSlug: stringValue(data.espnLeagueSlug),
    primaryColorHex: stringValue(data.primaryColorHex),
    secondaryColorHex: stringValue(data.secondaryColorHex),
    createdAt: dateValue(data.createdAt),
    updatedAt: dateValue(data.updatedAt),
  };
}

export function leagueFirestoreData(league: LeagueModel): Record<string, unknown> {
  return compactData({
    id: league.id,
    sportID: league.sportID,
    slug: league.slug,
    name: league.name,
    abbreviation: league.abbreviation,
    source: league.source,
    reviewStatus: league.reviewStatus,
    managedBy: league.managedBy,
    creatorUserID: league.creatorUserID,
    adminUserIDs: league.adminUserIDs,
    espnLeagueSlug: league.espnLeagueSlug,
    primaryColorHex: league.primaryColorHex,
    secondaryColorHex: league.secondaryColorHex,
    createdAt: league.createdAt,
    updatedAt: league.updatedAt,
  });
}
